import { defineStore } from "pinia";
import type { User } from "oidc-client-ts";
import { computed, nextTick, ref } from "vue";
import { useOpenIdAuthenticationStore } from "@/stores/AppStartUp/OpenIdAuthenticationStore";
import { useAccessTokenStore } from "@/stores/AppStartUp/AccessTokenStore";
import { DOWNLOAD_LIMIT_DEFAULT } from "@/config";
import { apiLibrary } from "@/api";

export const useAuthUserStore = defineStore("authUserStore", () => {
    const authUser = ref<User | null>();
    const authUserIsInitialized = ref<boolean>(false);
    const initializationWasExecuted = ref<boolean>(false);

    const accessTokenStore = useAccessTokenStore();

    const { getUser } = useOpenIdAuthenticationStore();

    const initialize = async () => {
        try {
            authUser.value = await getUser();

            if (authUser.value) {
                authUserIsInitialized.value = true;
            }

            await nextTick();

            await accessTokenStore.saveAccessToken(authUser.value?.access_token || "");
            accessTokenStore.setExpireDate(authUser.value?.expires_at as number);
        } catch (error) {
            console.error("AuthUserStore: Failed to initialize auth user", error);
            authUser.value = null;
        } finally {
            initializationWasExecuted.value = true;
        }
    };

    const authUserIsLoaded = computed(() => authUserIsInitialized.value && !!authUser.value);

    const profile = computed(() => authUser.value?.profile);
    const accessToken = computed(() => authUser.value?.access_token);
    const roles = computed(() => {
        const profile = authUser.value?.profile;
        if (!profile) return [];

        const profileGroups = profile.groups as string[] | undefined;
        const profileRoles = profile.roles as string[] | undefined;

        const finalRoles = profileGroups || profileRoles;
        if (!finalRoles) return [];

        return [...finalRoles];
    });

    const downloadLimitState = ref<number>(DOWNLOAD_LIMIT_DEFAULT);
    const downloadLimit = computed(() => downloadLimitState.value);

    const fetchDownloadLimit = async () => {
        try {
            const { data } = await apiLibrary.datasheet.documentsDownload.getDownloadLimit();

            downloadLimitState.value = data.downloadLimit ?? Infinity;
        } catch (error) {
            console.error("AuthUserStore: Failed to fetch download limit", error);
        }
    };

    const reset = async () => {
        authUser.value = undefined;

        await nextTick();
    };

    return {
        initialize,
        authUser,
        authUserIsLoaded,
        profile,
        accessToken,
        roles,
        downloadLimit,
        fetchDownloadLimit,
        initializationWasExecuted,
        reset
    };
});
